import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createTargetGroup,
  deleteTargetGroup,
  fetchTargetGroupById,
  fetchTargetGroups,
  updateTargetGroup,
} from './TargetGroups.services.ts';
import { toast } from 'react-toastify';
import i18n from '../../utils/i18n.ts';
import type { ApiError } from '../../types/App.ts';
import { useMsal } from '@azure/msal-react';

export const useService = ({ targetGroupId }: { targetGroupId: string | null }) => {
  const { accounts } = useMsal();
  const queryClient = useQueryClient();

  const hasAccount = accounts.length > 0;

  const createMutation = useMutation({
    mutationFn: createTargetGroup,
    onSuccess: () => {
      toast.success(i18n.t('success.api.post-item', { item: i18n.t('module.target-group') }));
      queryClient.invalidateQueries({ queryKey: ['targetGroups'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateTargetGroup,
    onSuccess: () => {
      toast.success(i18n.t('success.api.put-item', { item: i18n.t('module.target-group') }));
      queryClient.invalidateQueries({ queryKey: ['targetGroups'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTargetGroup,
    onSuccess: () => {
      toast.success(i18n.t('success.api.delete-item', { item: i18n.t('module.target-group') }));
      queryClient.invalidateQueries({ queryKey: ['targetGroups'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });

  const { data: groups, isLoading } = useQuery({
    queryKey: ['targetGroups', { showInactive: true }],
    queryFn: () => fetchTargetGroups({ showInactive: true }),
    enabled: hasAccount,
    retry: false,
  });

  const { data: group } = useQuery({
    queryKey: ['targetGroup', targetGroupId],
    queryFn: () => fetchTargetGroupById(targetGroupId),
    enabled: !!targetGroupId && hasAccount,
    retry: false,
  });

  return { createMutation, updateMutation, deleteMutation, groups, group, isLoading };
};